// ============================================================================
// useMarketRole.ts — КТО ПОЛЬЗОВАТЕЛЬ В МАРКЕТПЛЕЙСЕ: клиент или мастер.
//
// Роли не путаем:
//   • клиент — витрина и мастер заявки (создание заявки);
//   • мастер — СВОЙ КАБИНЕТ (лента свободных заявок + взятые им заявки).
//
// Источник истины по личности мастера — связь справочника «Мастера»
// (mkt_masters.entity_id = текущий пользователь). Право чтения справочника
// проверяется через метаданные (GetCatalog.rights) ДО запроса строк — у
// клиента прав на справочник мастеров обычно нет, и лишний 403 не создаётся.
//
// Ошибки не подменяются «клиентом»: нет связи без кэша → явная ошибка с
// повтором. Кэш роли (по базе и пользователю) помогает работе без сети.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import {
  isNotFoundError,
  isPermissionDeniedError,
  isTransportError,
  logErrorToJournal,
  logSwallowed,
} from '@/lib/errors';
import { listTableRows, toMarketFailure, type MarketFailure } from '@/lib/market';
import { MARKET_MASTER_TABLE } from '@/lib/marketConstants';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';

export type MarketRole = 'client' | 'master';

export interface MarketRoleState {
  status: 'loading' | 'ready' | 'error';
  role: MarketRole;
  failure: MarketFailure | null;
  /** Роль взята из локального кэша (сеть недоступна) — интерфейс работает. */
  cached: boolean;
  retry: () => void;
}

const CACHE_PREFIX = 'hydra_market_role:';

function baseKey(): string {
  return useCenterStore.getState().baseSlug || 'local';
}

function cacheKey(userId: string): string {
  return `${CACHE_PREFIX}${baseKey()}:${userId}`;
}

function readCachedRole(userId: string): MarketRole | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw === 'master' || raw === 'client' ? raw : null;
  } catch (e) {
    logSwallowed(e, 'useMarketRole: чтение кэша роли');
    return null;
  }
}

function writeCachedRole(userId: string, role: MarketRole): void {
  try {
    localStorage.setItem(cacheKey(userId), role);
  } catch (e) {
    logSwallowed(e, 'useMarketRole: сохранение кэша роли');
  }
}

async function detectRole(userId: string): Promise<MarketRole> {
  const sdk = getSDK();
  // 1) Право чтения справочника мастеров (метаданные, без запроса строк).
  const catalog: any = await sdk.metadata.getCatalog({ id: MARKET_MASTER_TABLE });
  if (catalog?.rights?.canRead === false) return 'client';

  // 2) Мастер — тот, кто связан с пользователем в справочнике «Мастера».
  const rows = await listTableRows(MARKET_MASTER_TABLE, {
    pageSize: 1,
    filters: [{ column: 'entity_id', value: userId }],
  });
  return rows.length > 0 ? 'master' : 'client';
}

/**
 * Роль пользователя в маркетплейсе.
 * @param userId текущий пользователь ('' — гость: роль не определяется)
 * @param enabled выполнять проверку (только после входа)
 */
export function useMarketRole(userId: string, enabled: boolean): MarketRoleState {
  const cachedOnMount = userId ? readCachedRole(userId) : null;
  const [status, setStatus] = useState<MarketRoleState['status']>(
    !enabled || !userId ? 'ready' : cachedOnMount ? 'ready' : 'loading',
  );
  const [role, setRole] = useState<MarketRole>(cachedOnMount || 'client');
  const [cached, setCached] = useState(Boolean(cachedOnMount));
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [tick, setTick] = useState(0);

  const retry = useCallback(() => {
    setFailure(null);
    setCached(false);
    setStatus('loading');
    setTick((n) => n + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !userId) {
      setStatus('ready');
      setRole('client');
      setCached(false);
      setFailure(null);
      return;
    }

    let cancelled = false;
    const known = readCachedRole(userId);
    (async () => {
      try {
        const detected = await detectRole(userId);
        if (cancelled) return;
        writeCachedRole(userId, detected);
        setRole(detected);
        setCached(false);
        setFailure(null);
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        // Нет прав на справочник мастеров / справочника нет — это КЛИЕНТ,
        // а не ошибка витрины: заявки клиента от этого не зависят.
        if (isPermissionDeniedError(e)) {
          writeCachedRole(userId, 'client');
          setRole('client');
          setCached(false);
          setFailure(null);
          setStatus('ready');
          return;
        }
        if (isNotFoundError(e)) {
          logSwallowed(e, 'useMarketRole: справочник мастеров недоступен');
          writeCachedRole(userId, 'client');
          setRole('client');
          setCached(false);
          setFailure(null);
          setStatus('ready');
          return;
        }
        // Нет связи — работаем по последней известной роли (если она есть).
        if (isTransportError(e)) {
          const fallback = readCachedRole(userId) || known;
          if (fallback) {
            setRole(fallback);
            setCached(true);
            setFailure(null);
            setStatus('ready');
            return;
          }
        }
        logErrorToJournal(e, 'Витрина маркетплейса: определение роли');
        setFailure(toMarketFailure(e));
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, userId, tick]);

  return { status, role, failure, cached, retry };
}
